// material/view — the generation side view, a PURE projection: a Shown focus
// in, Msgs out (`onMsg`). No I/O, no effects; the only local state is the
// per-part feedback draft (view-ephemeral: it survives no reload and means
// nothing to the domain until sent as `revision_requested`).
//
// Layout (calm, borderless, progressive disclosure):
//   header   — kind chip + AI marker (once a part is AI-written), the material
//              title, one quiet status line, a thin progress bar while running
//   controls — the ONE action the job state offers (Generate = the author's
//              approval of the skeleton / Stop / Continue), plus a plain-words
//              problem line when an intent failed
//   parts    — reading order; each row a status dot + title + status word;
//              a readable part opens in place (MaterialPreview) with
//              "Ge feedback" under it; a landed part shows one quiet line of
//              what it was checked against (automatic vs judged by AI),
//              opening to the items.
//   phase 3  — an outdated part says which dependency changed, with
//              Regenerate / Still fine (calm, never alarm-styled); the header
//              sums them up with "Regenerate all outdated"; an opened part
//              lists what it builds on; an older pinned version says so.
//   phase 5a — the author's review: a written part offers a calm "Approve";
//              an approved one shows a check mark and "Reviewed" (undo via
//              the part's menu); a part changed since its approval says so;
//              the header counts "3 of 5 parts reviewed".

import { Plural, Trans, useLingui } from "@lingui/react/macro";
import { type ReactNode, useState } from "react";
import type { PartCheck } from "../../app/ports.js";
import { AiMarker } from "../../components/AiMarker/AiMarker.js";
import { Badge } from "../../components/Badge/Badge.js";
import { Button } from "../../components/Button/Button.js";
import { Heading } from "../../components/Heading/Heading.js";
import { Icon } from "../../components/Icon/Icon.js";
import { MaterialPreview } from "../../components/MaterialPreview/MaterialPreview.js";
import { ProgressBar } from "../../components/ProgressBar/ProgressBar.js";
import { RowOverflow } from "../../components/RowOverflow/RowOverflow.js";
import { Skeleton } from "../../components/Skeleton/Skeleton.js";
import { Text } from "../../components/Text/Text.js";
import { Textarea } from "../../components/Textarea/Textarea.js";
import type { JobIntent, Shown } from "./model.js";
import type { MaterialMsg } from "./msg.js";
import { StatusLine } from "./StatusLine.js";
import {
    type FailureKind,
    hasAiParts,
    type JobControls,
    jobControls,
    jobStatusOf,
    type PartChecks,
    type PartRow,
    partRows,
    type RowStatus,
    reviewProgress,
    type StaleSummary,
    staleSummary,
} from "./view-model.js";
import "./view.css";

export type GenerationSideViewProps = {
    readonly focus: Shown;
    readonly onMsg: (msg: MaterialMsg) => void;
};

export const GenerationSideView = ({
    focus,
    onMsg,
}: GenerationSideViewProps): ReactNode => {
    const { t } = useLingui();
    const status = jobStatusOf(focus);
    const title = focus.root.title;
    return (
        <section className="bilda-gen" aria-label={t`Parts of ${title}`}>
            <header className="bilda-gen__head">
                <div className="bilda-gen__chips">
                    <Badge icon={<Icon name="material" size="sm" />}>
                        {t({ message: "Material", context: "resource kind" })}
                    </Badge>
                    {hasAiParts(focus) ? (
                        <AiMarker
                            variant="chip"
                            title={t`These parts were written with AI. Review them before you rely on them.`}
                        />
                    ) : null}
                </div>
                <Heading
                    level={2}
                    size="lg"
                    className="bilda-heading--clamp"
                    title={title}
                >
                    {title}
                </Heading>
                <div aria-live="polite">
                    <Text as="p" size="sm" tone="dim">
                        <StatusLine status={status} />
                    </Text>
                </div>
                <ReviewLine focus={focus} />
                {status._tag === "Status.Running" ? (
                    <ProgressBar
                        tone="accent"
                        value={
                            status.total === 0
                                ? 0
                                : (status.landed / status.total) * 100
                        }
                        label={t`Parts ready`}
                    />
                ) : null}
            </header>
            <Controls controls={jobControls(focus)} onMsg={onMsg} />
            <StaleHeader summary={staleSummary(focus)} onMsg={onMsg} />
            <Problem focus={focus} />
            <ol className="bilda-gen__parts">
                {partRows(focus).map((row) => (
                    <PartItem
                        key={row.id}
                        row={row}
                        focus={focus}
                        onMsg={onMsg}
                    />
                ))}
            </ol>
        </section>
    );
};

// How far the author's review has come; silent until a part can be reviewed.
const ReviewLine = ({ focus }: { readonly focus: Shown }): ReactNode => {
    const progress = reviewProgress(focus);
    if (progress === null) return null;
    const { reviewed, total } = progress;
    return (
        <Text as="p" size="sm" tone="dim">
            <Trans>
                {reviewed} of {total} parts reviewed
            </Trans>
        </Text>
    );
};

const Controls = ({
    controls,
    onMsg,
}: {
    readonly controls: JobControls;
    readonly onMsg: (msg: MaterialMsg) => void;
}): ReactNode => {
    const { t } = useLingui();
    switch (controls._tag) {
        case "Controls.None":
        case "Controls.Waiting":
            return null;
        case "Controls.Generate":
            return (
                <div className="bilda-gen__controls">
                    <Button
                        variant="primary"
                        size="sm"
                        disabled={controls.pending}
                        onClick={() => onMsg({ type: "generate_requested" })}
                    >
                        {controls.pending
                            ? t`Starting…`
                            : controls.resume
                              ? t`Generate the rest`
                              : t`Generate`}
                    </Button>
                    <Text as="p" size="xs" tone="dim">
                        <Trans>
                            Writes the planned parts, a few at a time. You can
                            stop whenever you like.
                        </Trans>
                    </Text>
                </div>
            );
        case "Controls.Running":
            return (
                <div className="bilda-gen__controls">
                    <Button
                        variant="secondary"
                        size="sm"
                        disabled={controls.stopping}
                        onClick={() => onMsg({ type: "stop_requested" })}
                    >
                        {controls.stopping ? t`Stopping…` : t`Stop`}
                    </Button>
                    <Text as="p" size="xs" tone="dim">
                        <Trans>Finished parts stay when you stop.</Trans>
                    </Text>
                </div>
            );
        case "Controls.Interrupted":
            return (
                <div className="bilda-gen__controls">
                    <Button
                        variant="primary"
                        size="sm"
                        disabled={controls.pending}
                        onClick={() => onMsg({ type: "continue_requested" })}
                    >
                        {controls.pending ? t`Continuing…` : t`Continue`}
                    </Button>
                    <Text as="p" size="xs" tone="dim">
                        <Plural
                            value={controls.remaining}
                            one="Writing was interrupted. Finished parts are kept; # part is left."
                            other="Writing was interrupted. Finished parts are kept; # parts are left."
                        />
                    </Text>
                </div>
            );
        case "Controls.Paused":
            return (
                <div className="bilda-gen__controls">
                    <Text as="p" size="sm">
                        <Plural
                            value={controls.remaining}
                            one="# part is left. Keep going?"
                            other="# parts are left. Keep going?"
                        />
                    </Text>
                    <div className="bilda-gen__buttons">
                        <Button
                            variant="primary"
                            size="sm"
                            disabled={controls.pending}
                            onClick={() =>
                                onMsg({ type: "continue_requested" })
                            }
                        >
                            {controls.pending ? t`Continuing…` : t`Continue`}
                        </Button>
                        <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => onMsg({ type: "stop_requested" })}
                        >
                            {t`Stop`}
                        </Button>
                    </div>
                </div>
            );
    }
};

// One calm line when parts are outdated: the author decides, nothing is
// rewritten on its own.
const StaleHeader = ({
    summary,
    onMsg,
}: {
    readonly summary: StaleSummary | null;
    readonly onMsg: (msg: MaterialMsg) => void;
}): ReactNode => {
    const { t } = useLingui();
    if (summary === null) return null;
    return (
        <div className="bilda-gen__controls">
            <Text as="p" size="sm">
                <Plural
                    value={summary.parts.length}
                    one="# part may need updating"
                    other="# parts may need updating"
                />
            </Text>
            {summary.canRegenerate ? (
                <div className="bilda-gen__buttons">
                    <Button
                        variant="secondary"
                        size="sm"
                        onClick={() =>
                            onMsg({
                                type: "regenerate_requested",
                                parts: summary.parts,
                            })
                        }
                    >
                        {t`Regenerate all outdated`}
                    </Button>
                </div>
            ) : null}
        </div>
    );
};

const Problem = ({ focus }: { readonly focus: Shown }): ReactNode => {
    const p = focus.problem;
    if (p._tag === "Problem.None") return null;
    return (
        <div role="status" className="bilda-gen__problem">
            <Text as="p" size="sm" tone="danger">
                {p._tag === "Problem.Ungrounded" ? (
                    <Trans>
                        This material has no sources yet. Add a source first:
                        every part is written from your sources.
                    </Trans>
                ) : (
                    <ProblemText intent={p.intent._tag} />
                )}
            </Text>
        </div>
    );
};

const ProblemText = ({
    intent,
}: {
    readonly intent: JobIntent["_tag"];
}): ReactNode => {
    switch (intent) {
        case "Intent.Start":
            return <Trans>Couldn't start. Try again.</Trans>;
        case "Intent.Stop":
            return <Trans>Couldn't stop. Try again.</Trans>;
        case "Intent.Continue":
            return <Trans>Couldn't continue. Try again.</Trans>;
        case "Intent.Revise":
            return <Trans>Couldn't send your feedback. Try again.</Trans>;
        case "Intent.StillFine":
            return <Trans>Couldn't keep the part as it is. Try again.</Trans>;
        case "Intent.UseVersion":
            return (
                <Trans>Couldn't switch to the newer version. Try again.</Trans>
            );
        case "Intent.KeepVersion":
            return <Trans>Couldn't keep this version. Try again.</Trans>;
        case "Intent.Approve":
            return <Trans>Couldn't approve the part. Try again.</Trans>;
        case "Intent.RevokeApproval":
            return <Trans>Couldn't undo the approval. Try again.</Trans>;
    }
};

const StatusWord = ({ status }: { readonly status: RowStatus }): ReactNode => {
    switch (status) {
        case "planned":
            return <Trans>Planned</Trans>;
        case "written":
            return <Trans>Written</Trans>;
        case "removed":
            return <Trans>Removed</Trans>;
        case "queued":
            return <Trans>Waiting</Trans>;
        case "running":
            return <Trans>Writing…</Trans>;
        case "landed":
            return <Trans>Ready to review</Trans>;
        case "failed":
            return <Trans>Couldn't be written</Trans>;
        case "cancelled":
            return <Trans>Stopped</Trans>;
        case "kept":
            return <Trans>Your version is kept</Trans>;
    }
};

const FailureText = ({ kind }: { readonly kind: FailureKind }): ReactNode => {
    switch (kind) {
        case "unfinished":
            return (
                <Trans>The draft still had problems after several tries.</Trans>
            );
        case "not_saved":
            return <Trans>The part couldn't be saved.</Trans>;
        case "no_answer":
            return <Trans>The assistant didn't answer as expected.</Trans>;
    }
};

const PartItem = ({
    row,
    focus,
    onMsg,
}: {
    readonly row: PartRow;
    readonly focus: Shown;
    readonly onMsg: (msg: MaterialMsg) => void;
}): ReactNode => {
    const { t } = useLingui();
    const key = row.contentKey;
    const head = (
        <>
            <span
                className={`bilda-gen__dot bilda-gen__dot--${row.status}`}
                aria-hidden="true"
            />
            <span className="bilda-gen__title">{row.title}</span>
            {row.review === "reviewed" ? (
                <Text as="span" size="xs" className="bilda-gen__reviewed">
                    <Icon name="check" size="sm" />
                    <Trans>Reviewed</Trans>
                </Text>
            ) : (
                <Text as="span" size="xs" tone="dim">
                    <StatusWord status={row.status} />
                </Text>
            )}
        </>
    );
    return (
        <li
            className={`bilda-gen__part bilda-gen__part--${row.status}`}
            aria-busy={row.status === "running"}
        >
            {key === null ? (
                <div className="bilda-gen__row">{head}</div>
            ) : (
                <button
                    type="button"
                    className="bilda-gen__row bilda-gen__row--open"
                    aria-expanded={row.expanded}
                    onClick={() =>
                        onMsg(
                            row.expanded
                                ? { type: "part_closed", part: row.id }
                                : { type: "part_opened", part: row.id, key },
                        )
                    }
                >
                    {head}
                </button>
            )}
            {row.revokeVersion !== null ? (
                <RowOverflow
                    label={t`Actions for ${row.title}`}
                    items={[
                        {
                            _tag: "item",
                            label: t`Undo approval`,
                            onSelect: () =>
                                onMsg({
                                    type: "approval_revoke_requested",
                                    part: row.id,
                                }),
                        },
                    ]}
                />
            ) : null}
            {row.status === "running" ? (
                <span className="bilda-gen__pulse" aria-hidden="true" />
            ) : null}
            {row.failure !== null ? (
                <Text as="p" size="xs" tone="dim" className="bilda-gen__note">
                    <FailureText kind={row.failure} />
                </Text>
            ) : null}
            {row.checks !== null ? <ChecksView checks={row.checks} /> : null}
            <StaleView row={row} onMsg={onMsg} />
            <ReviewView row={row} onMsg={onMsg} />
            {row.newerVersions !== null ? (
                <NewerVersionsNote
                    part={row.id}
                    title={row.title}
                    heads={row.newerVersions.heads}
                    onMsg={onMsg}
                />
            ) : null}
            {row.expanded && key !== null ? (
                <div className="bilda-gen__body">
                    <DependencyChips row={row} onMsg={onMsg} />
                    <PartContentView
                        focus={focus}
                        row={row}
                        contentKey={key}
                        onMsg={onMsg}
                    />
                    {row.canRevise || row.revising ? (
                        <FeedbackBox row={row} onMsg={onMsg} />
                    ) : null}
                </div>
            ) : null}
        </li>
    );
};

// The author's review of a written part: a calm "Approve" (a secondary
// action, never a call to alarm), and a quiet note when the part changed
// since its approval.
const ReviewView = ({
    row,
    onMsg,
}: {
    readonly row: PartRow;
    readonly onMsg: (msg: MaterialMsg) => void;
}): ReactNode => {
    const { t } = useLingui();
    if (row.review === null || row.review === "reviewed") return null;
    return (
        <div className="bilda-gen__review">
            {row.review === "stale_after_review" ? (
                <Text as="p" size="xs" tone="dim">
                    <Trans>Changed since you approved it</Trans>
                </Text>
            ) : null}
            {row.approveVersion !== null ? (
                <Button
                    variant="ghost"
                    size="sm"
                    onClick={() =>
                        onMsg({ type: "approve_requested", part: row.id })
                    }
                >
                    {t`Approve`}
                </Button>
            ) : null}
        </div>
    );
};

// An outdated part: one calm line naming what changed (several open to the
// list) and the author's two answers. Never alarm-styled: nothing is wrong,
// the author decides.
const StaleView = ({
    row,
    onMsg,
}: {
    readonly row: PartRow;
    readonly onMsg: (msg: MaterialMsg) => void;
}): ReactNode => {
    const { t } = useLingui();
    if (row.stillFine !== null)
        return (
            <div role="status">
                <Text as="p" size="xs" tone="dim" className="bilda-gen__note">
                    <Trans>Keeping it as it is…</Trans>
                </Text>
            </div>
        );
    const [first, ...rest] = row.stale;
    if (first === undefined) return null;
    const dependency = first.dependencyTitle;
    const count = row.stale.length;
    return (
        <div className="bilda-gen__stale">
            {rest.length === 0 ? (
                <Text as="p" size="xs" tone="dim">
                    {t`Outdated: ${dependency} has changed`}
                </Text>
            ) : (
                <details className="bilda-gen__stale-more">
                    <summary>
                        <Text as="span" size="xs" tone="dim">
                            {t`Outdated: ${count} dependencies have changed`}
                        </Text>
                    </summary>
                    <ul className="bilda-gen__check-list">
                        {row.stale.map((s) => (
                            <li key={s.dependency}>
                                <Text as="span" size="xs">
                                    {s.dependencyTitle}
                                </Text>
                            </li>
                        ))}
                    </ul>
                </details>
            )}
            <div className="bilda-gen__buttons">
                {row.canRegenerate ? (
                    <Button
                        variant="secondary"
                        size="sm"
                        onClick={() =>
                            onMsg({
                                type: "regenerate_requested",
                                parts: [row.id],
                            })
                        }
                    >
                        {t`Regenerate`}
                    </Button>
                ) : null}
                <Button
                    variant="ghost"
                    size="sm"
                    onClick={() =>
                        onMsg({ type: "still_fine_requested", part: row.id })
                    }
                >
                    {t`Still fine`}
                </Button>
            </div>
        </div>
    );
};

// The include pins an older version of this part than its newest: the
// author uses the newer one or keeps this one. Branches (several newer
// versions) list each, to pick from. Calm, like an outdated part.
const NewerVersionsNote = ({
    part,
    title,
    heads,
    onMsg,
}: {
    readonly part: string;
    readonly title: string;
    readonly heads: readonly number[];
    readonly onMsg: (msg: MaterialMsg) => void;
}): ReactNode => {
    const { t } = useLingui();
    const count = heads.length;
    const use = (version: number) => () =>
        onMsg({ type: "newer_version_chosen", part, version });
    const keep = (
        <Button
            variant="ghost"
            size="sm"
            onClick={() => onMsg({ type: "version_kept", part })}
        >
            {t`Keep`}
        </Button>
    );
    const [only] = heads;
    return (
        <div className="bilda-gen__stale">
            <Text as="p" size="xs" tone="dim">
                {count === 1
                    ? t`There is a newer version of ${title}`
                    : t`There are ${count} newer versions of ${title}`}
            </Text>
            {count === 1 && only !== undefined ? (
                <div className="bilda-gen__buttons">
                    <Button variant="secondary" size="sm" onClick={use(only)}>
                        {t`Use latest`}
                    </Button>
                    {keep}
                </div>
            ) : (
                <>
                    <ul className="bilda-gen__check-list">
                        {heads.map((version) => (
                            <li key={version} className="bilda-gen__buttons">
                                <Text as="span" size="xs">
                                    {t`Version ${version}`}
                                </Text>
                                <Button
                                    variant="secondary"
                                    size="sm"
                                    onClick={use(version)}
                                >
                                    {t`Use this one`}
                                </Button>
                            </li>
                        ))}
                    </ul>
                    <div className="bilda-gen__buttons">{keep}</div>
                </>
            )}
        </div>
    );
};

// What an opened part builds on. A dependency that is a part of this
// material opens in the panel; any other is named.
const DependencyChips = ({
    row,
    onMsg,
}: {
    readonly row: PartRow;
    readonly onMsg: (msg: MaterialMsg) => void;
}): ReactNode => {
    if (row.buildsOn.length === 0) return null;
    return (
        <div className="bilda-gen__deps">
            <Text as="span" size="xs" tone="dim">
                <Trans>Builds on:</Trans>
            </Text>
            {row.buildsOn.map((d) => {
                const ref = d.ref;
                return ref === null ? (
                    <span key={d.id} className="bilda-gen__dep">
                        {d.title}
                    </span>
                ) : (
                    <button
                        key={d.id}
                        type="button"
                        className="bilda-gen__dep bilda-gen__dep--open"
                        onClick={() =>
                            onMsg({ type: "dependency_opened", ref })
                        }
                    >
                        {d.title}
                    </button>
                );
            })}
        </div>
    );
};

// What the part was checked against: one calm line ("Checked: 3 automatic,
// 2 judged by AI – 1 to look at") opening (native disclosure, no state) to
// the items, each in plain words. AI-judged items carry the AI marker and
// the AI's own one-line reason.
const ChecksView = ({ checks }: { readonly checks: PartChecks }): ReactNode => {
    const { t } = useLingui();
    return (
        <details className="bilda-gen__checks">
            <summary>
                <Text as="span" size="xs" tone="dim">
                    <Plural
                        value={checks.computed}
                        one="Checked: # automatic"
                        other="Checked: # automatic"
                    />
                    {checks.judged > 0 ? (
                        <>
                            {", "}
                            <Plural
                                value={checks.judged}
                                one="# judged by AI"
                                other="# judged by AI"
                            />
                        </>
                    ) : null}
                    {checks.attention > 0 ? (
                        <>
                            {" – "}
                            <Plural
                                value={checks.attention}
                                one="# to look at"
                                other="# to look at"
                            />
                        </>
                    ) : null}
                </Text>
            </summary>
            <ul className="bilda-gen__check-list">
                {checks.items.map((c) => (
                    <li
                        key={c.name}
                        className={`bilda-gen__check bilda-gen__check--${c.outcome}`}
                    >
                        <span className="bilda-gen__check-dot" aria-hidden />
                        <span className="bilda-gen__check-text">
                            <Text as="span" size="xs">
                                <CheckText check={c} />
                            </Text>
                            {c.evaluator === "judged" && c.note.length > 0 ? (
                                <Text as="span" size="xs" tone="dim">
                                    {c.note}
                                </Text>
                            ) : null}
                        </span>
                        {c.evaluator === "judged" ? (
                            <AiMarker
                                variant="tag"
                                title={t`Judged by AI. It can be wrong: check it yourself.`}
                            />
                        ) : (
                            <Text as="span" size="xs" tone="dim">
                                {t`Automatic`}
                            </Text>
                        )}
                    </li>
                ))}
            </ul>
        </details>
    );
};

const CheckText = ({ check }: { readonly check: PartCheck }): ReactNode => {
    const passed = check.outcome === "passed";
    switch (check.name) {
        case "template_rules":
            return passed ? (
                <Trans>Follows the template's rules</Trans>
            ) : (
                <Trans>Breaks some of the template's rules</Trans>
            );
        case "citations":
            return passed ? (
                <Trans>
                    Every question and passage is backed by your uploaded
                    sources
                </Trans>
            ) : (
                <Plural
                    value={check.unsupported}
                    one="# question or passage lacks support in your uploaded sources"
                    other="# questions or passages lack support in your uploaded sources"
                />
            );
        case "knowledge_gaps": {
            const gaps = check.gaps.join("; ");
            return (
                <Trans>
                    Needs knowledge your uploaded sources lack: {gaps}
                </Trans>
            );
        }
        case "readability": {
            const sections = check.heavySections.join(", ");
            return passed ? (
                <Trans>Not too hard to read</Trans>
            ) : (
                <Trans>Hard to read: {sections}</Trans>
            );
        }
        case "covers_brief":
            return passed ? (
                <Trans>Does what its plan says</Trans>
            ) : (
                <Trans>Misses part of its plan</Trans>
            );
        case "faithful_to_sources":
            return passed ? (
                <Trans>Says only what the sources support</Trans>
            ) : (
                <Trans>May say more than the sources support</Trans>
            );
    }
};

const PartContentView = ({
    focus,
    row,
    contentKey,
    onMsg,
}: {
    readonly focus: Shown;
    readonly row: PartRow;
    readonly contentKey: string;
    readonly onMsg: (msg: MaterialMsg) => void;
}): ReactNode => {
    const { t } = useLingui();
    const content = focus.contents.get(contentKey);
    if (content === undefined || content._tag === "Remote.Loading")
        return (
            <div
                className="bilda-gen__loading"
                role="status"
                aria-label={t`Loading`}
            >
                <Skeleton width="70%" />
                <Skeleton width="90%" />
                <Skeleton width="80%" />
            </div>
        );
    if (content._tag === "Remote.Failed")
        return (
            <div className="bilda-gen__note">
                <Text as="p" size="sm" tone="dim">
                    <Trans>Couldn't load this part.</Trans>
                </Text>
                <Button
                    variant="ghost"
                    size="sm"
                    onClick={() =>
                        onMsg({
                            type: "part_opened",
                            part: row.id,
                            key: contentKey,
                        })
                    }
                >
                    {t`Try again`}
                </Button>
            </div>
        );
    return (
        <MaterialPreview
            title={row.title}
            elements={content.value.elements}
            provenance={content.value.provenance}
        />
    );
};

const FeedbackBox = ({
    row,
    onMsg,
}: {
    readonly row: PartRow;
    readonly onMsg: (msg: MaterialMsg) => void;
}): ReactNode => {
    const { t } = useLingui();
    const [open, setOpen] = useState(false);
    const [text, setText] = useState("");
    if (row.revising)
        return (
            <div role="status">
                <Text as="p" size="sm" tone="dim">
                    <Trans>Asking for a new version…</Trans>
                </Text>
            </div>
        );
    if (!open)
        return (
            <Button variant="ghost" size="sm" onClick={() => setOpen(true)}>
                {t`Give feedback`}
            </Button>
        );
    return (
        <form
            className="bilda-gen__feedback"
            onSubmit={(e) => {
                e.preventDefault();
                onMsg({
                    type: "revision_requested",
                    part: row.id,
                    feedback: text,
                });
                setOpen(false);
                setText("");
            }}
        >
            <Textarea
                size="sm"
                rows={3}
                value={text}
                aria-label={t`What should change in ${row.title}?`}
                placeholder={t`What should change?`}
                onChange={(e) => setText(e.target.value)}
            />
            <div className="bilda-gen__buttons">
                <Button
                    type="submit"
                    variant="primary"
                    size="sm"
                    disabled={text.trim().length === 0}
                >
                    {t`Ask for a new version`}
                </Button>
                <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                        setOpen(false);
                        setText("");
                    }}
                >
                    {t`Cancel`}
                </Button>
            </div>
        </form>
    );
};
